import {isFeatureEnabled} from '@github-ui/feature-flags'
import type React from 'react'
import {createContext, use, useMemo} from 'react'

import {
  formatQuotaResetLabel,
  isNotNullOrUndefined,
  useCopilotChatEntitlement,
} from '../../utils/copilot-chat-entitlement'
import {type CopilotChatEntitlementQuotas, CopilotLicenseType, CopilotPlan} from '../../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../../utils/copilot-feature-flags'

interface EntitlementContextType {
  licenseType: CopilotLicenseType | null
  plan: CopilotPlan | null
  chatQuotaRemaining: number
  premiumChatQuotaRemaining: number
  resetDate: string
  isLicensedLimited: boolean
  chatQuotaExceeded: boolean
  premiumInteractionsQuotaExceeded: boolean
  reloadQuota: (origin?: string) => void
  canPurchaseAdditionalQuota: boolean
  canUpgradePlan: boolean
  overagesEnabled: boolean
  eligibleForTrial: boolean
  sessionQuotaExceeded: boolean
  sessionQuotaRemaining: number | undefined
  sessionQuotaResetLabel: string
  weeklyQuotaExceeded: boolean
  weeklyQuotaRemaining: number | undefined
  weeklyQuotaResetLabel: string
  tbbQuotaExceeded: boolean
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  isIndividualPaidOrStudentPlan: boolean
}

interface EntitlementProviderProps {
  children: React.ReactNode
  initialLicenseType: CopilotLicenseType
  initialPlan?: CopilotPlan
  initialQuotas?: CopilotChatEntitlementQuotas
}

export const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined)

export function EntitlementProvider({
  children,
  initialLicenseType,
  initialPlan,
  initialQuotas,
}: EntitlementProviderProps) {
  const [entitlement, reloadQuota] = useCopilotChatEntitlement(initialLicenseType, initialPlan, initialQuotas)
  const licenseType = entitlement.licenseType
  const plan = entitlement.plan ?? null

  const chatQuotaRemaining = entitlement.chatQuotaRemaining() ?? Infinity
  const premiumChatQuotaRemaining = entitlement.premiumInteractionsQuotaRemaining() ?? Infinity

  const resetDate = entitlement.quotas?.resetDate
    ? new Date(entitlement.quotas.resetDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  // Feature flag to simulate quota exceeded state for local development
  const simulateQuotaExceeded = isFeatureEnabled('copilot_simulate_quota_exceeded')

  const isLicensedLimited = simulateQuotaExceeded || licenseType === CopilotLicenseType.LicensedLimited

  const canPurchaseAdditionalQuota = entitlement.canPurchaseAdditionalQuota()
  const canUpgradePlan = entitlement.canUpgradePlan()

  const chatQuotaExceeded = simulateQuotaExceeded || (isLicensedLimited && chatQuotaRemaining <= 0)
  const premiumInteractionsQuotaExceeded =
    copilotFeatureFlags.premiumRequestQuotasEnabled && !isLicensedLimited && premiumChatQuotaRemaining <= 0
  const overagesEnabled =
    (copilotFeatureFlags.copilotDotcomChatTbb
      ? entitlement.quotas?.overageQuota?.permitted
      : entitlement.quotas?.overagesEnabled) ?? false

  // TBB Quota Limits
  const sessionQuotaRemaining = entitlement.quotas?.sessionQuota?.percentRemaining
  const sessionQuotaExceeded = isNotNullOrUndefined(sessionQuotaRemaining) && sessionQuotaRemaining <= 0
  const sessionQuotaResetLabel = formatQuotaResetLabel(entitlement.quotas?.sessionQuota?.resetAt)
  const weeklyQuotaRemaining = entitlement.quotas?.weeklyQuota?.percentRemaining
  const weeklyQuotaExceeded = isNotNullOrUndefined(weeklyQuotaRemaining) && weeklyQuotaRemaining <= 0
  const weeklyQuotaResetLabel = formatQuotaResetLabel(entitlement.quotas?.weeklyQuota?.resetAt)
  const overagesRemaining = entitlement.quotas?.overageQuota?.percentRemaining
  const overagesExceeded = isNotNullOrUndefined(overagesRemaining) && overagesRemaining <= 0
  const isIndividualPaidOrStudentPlan =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualStudent ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax
  const tbbQuotaExceeded =
    copilotFeatureFlags.copilotDotcomChatTbb &&
    (((sessionQuotaExceeded || weeklyQuotaExceeded) && !overagesEnabled) ||
      (overagesEnabled && (sessionQuotaExceeded || weeklyQuotaExceeded) && overagesExceeded))

  const eligibleForTrial = entitlement.eligibleForTrial()

  const value = useMemo(
    () => ({
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      sessionQuotaExceeded,
      sessionQuotaRemaining,
      sessionQuotaResetLabel,
      weeklyQuotaExceeded,
      weeklyQuotaRemaining,
      weeklyQuotaResetLabel,
      overagesRemaining,
      overagesExceeded,
      tbbQuotaExceeded,
      isIndividualPaidOrStudentPlan,
    }),
    [
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      sessionQuotaExceeded,
      sessionQuotaRemaining,
      sessionQuotaResetLabel,
      weeklyQuotaExceeded,
      weeklyQuotaRemaining,
      weeklyQuotaResetLabel,
      overagesRemaining,
      overagesExceeded,
      tbbQuotaExceeded,
      isIndividualPaidOrStudentPlan,
    ],
  )

  return <EntitlementContext value={value}>{children}</EntitlementContext>
}

export const useEntitlement = () => {
  const context = use(EntitlementContext)
  if (!context) {
    throw new Error('useEntitlement must be used within EntitlementProvider')
  }
  return context
}
